import React, { useState, useCallback, ChangeEvent, useRef, useEffect } from 'react';
import axios from 'axios';
import * as Constants from "../../constants"
import debounce from 'lodash/debounce';
import './Search.css';
import {ReactComponent as SearchIcon} from '../../assets/img/controls/search.svg';
import { LocationGeocode } from '../../services/locationGeocode/LocationGeocode';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { useSearchParams } from 'react-router-dom';


interface OptionType {
    label: string;
}

interface SearchPanelProps {
    setSelected: React.Dispatch<React.SetStateAction<string[]>>
}

const SearchPanel: React.FC<SearchPanelProps> = ({
    setSelected
}) => {
    const [options, setOptions] = useState<OptionType[]>([]);
    const [inputValue, setInputValue] = useState<string>('');
    const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);
    const [activeList, setActiveList] = useState<'suggestions' | 'recent'>('suggestions');
    const panelRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLUListElement>(null);
    const recentRef = useRef<HTMLUListElement>(null);
    const map = useMap();
    const [searchParams, setSearchParams] = useSearchParams();

    const handleLocationSelect = async (name: string) => {
        const key = searchParams.get("key");
        const geocode = new LocationGeocode();
        const coordinates = await geocode.forwardStadia(name, key);
        if (!coordinates) {
            return;
        }
        
        map.setView([coordinates.lat, coordinates.lon], 9);
        

        setRecentSearches(prevSearches => {
            const updatedSearches = prevSearches.filter(search => search.name !== name);

            updatedSearches.unshift({ name });

            if (updatedSearches.length > 7) {
                updatedSearches.pop();
            }
            localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
            return updatedSearches;
        });
    };

    const [recentSearches, setRecentSearches] = useState<{ name: string}[]>(() => {
        const savedData = localStorage.getItem('recentSearches');
        return savedData ? JSON.parse(savedData) : [
            {name: 'New York, NY, USA'},
            {name: 'Philadelphia, PA, USA'},
            {name: 'Boston, MA, USA'},
            {name: 'Austin, TX, USA'},
            {name: 'Fairfield, CT, USA'},
            {name: 'Orange, MA, USA'},
            {name: 'Toledo, OH, USA'},
        ];
    });

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const currentList = activeList === 'suggestions' ? options : recentSearches;
        const maxIndex = currentList.length - 1;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex(prev => {
                    const nextIndex = prev < maxIndex ? prev + 1 : 0;
                    scrollIntoView(nextIndex);
                    return nextIndex;
                });
                break;

            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex(prev => {
                    const nextIndex = prev > 0 ? prev - 1 : maxIndex;
                    scrollIntoView(nextIndex);
                    return nextIndex;
                });
                break;

            case 'Tab':
                e.preventDefault();
                // Switch between suggestions and recent lists
                setActiveList(prev => prev === 'suggestions' ? 'recent' : 'suggestions');
                setFocusedIndex(-1);
                break;

            case 'Enter':
                e.preventDefault();
                if (focusedIndex >= 0) {
                    if (activeList === 'suggestions' && options[focusedIndex]) {
                        handleSelect(options[focusedIndex]);
                    } else if (activeList === 'recent' && recentSearches[focusedIndex]) {
                        handleLocationClick(recentSearches[focusedIndex].name);
                    }
                }
                break;
        }
    };

    const scrollIntoView = (index: number) => {
        const listRef = activeList === 'suggestions' ? suggestionsRef : recentRef;
        if (listRef.current) {
            const listItems = listRef.current.getElementsByTagName('li');
            if (listItems[index]) {
                listItems[index].scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest'
                });
            }
        }
    };

    const fetchOptions = async (inputValue: string) => {
        if (!inputValue) {
            setOptions([]);
            setShowSuggestions(false);
            return;
        }
        try {
            const response = await axios.get(`${Constants.API_URL}?action=location&q=${inputValue}&limit=10&timestamp=${Date.now()}`);
            const data = response.data;
            if (data){
                const arrayData = data.split("\n");
                const options: {label: string}[] =[];
                 arrayData.forEach((item: string) => {
                    if (item){
                        options.push({
                            label: item,
                        });
                    }
                    
                });
                setOptions(options);
                setShowSuggestions(true);
                setActiveList('suggestions');
                setFocusedIndex(-1);
            }
        } catch (error) {
            console.error('Error fetching coordinates:', error);
        }
    };

    const debouncedFetchOptions = useCallback(debounce(fetchOptions, 500), []);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        debouncedFetchOptions(value);
    };

    const handleSelect = (option: OptionType) => {
        handleLocationSelect( option.label);
        setInputValue(option.label);
        setShowSuggestions(false);
        setFocusedIndex(-1);
        setSelected(prev => prev.filter(item => item !== 'search'));
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
            setShowSuggestions(false);
        }
    };

    const handleLocationClick = (name: string) => {
        handleLocationSelect(name);
        setInputValue(name);
        setShowSuggestions(false);
        setFocusedIndex(-1);
        setSelected(prev => prev.filter(item => item !== 'search'));
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        if (panelRef.current) {
            L.DomEvent.disableClickPropagation(panelRef.current);
            L.DomEvent.disableScrollPropagation(panelRef.current);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Focus input on component mount
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Reset focused index when options change
    useEffect(() => {
        setFocusedIndex(-1);
    }, [options]);

    return (
        <div className="search-panel" ref={panelRef}>
            <div className="search-input-wrapper">
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Search here..."
                    className="search-input"
                    role="combobox"
                    aria-expanded={showSuggestions}
                    aria-controls="search-suggestions"
                    aria-activedescendant={focusedIndex >= 0 ? `${activeList}-option-${focusedIndex}` : undefined}
                />
                <SearchIcon className="search-icon"/>
            </div>
            <hr className="divider"/>
            <div className={'content-result'}>
                {showSuggestions && (
                    <div className="search-results">
                        <ul
                            ref={suggestionsRef}
                            id="search-suggestions"
                            role="listbox"
                        >
                            {options.map((option, index) => (
                                <li
                                    key={index}
                                    onClick={() => handleSelect(option)}
                                    className={`search-option ${focusedIndex === index && activeList === 'suggestions' ? 'focused' : ''}`}
                                    id={`suggestions-option-${index}`}
                                    role="option"
                                    aria-selected={focusedIndex === index && activeList === 'suggestions'}
                                >
                                    {option.label}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="search-results">
                    <h4>Recent</h4>
                    <ul
                        ref={recentRef}
                        role="listbox"
                        id="recent-searches"
                    >
                        {recentSearches.map((search, index) => (
                            <li
                                key={index}
                                onClick={() => handleLocationClick(search.name)}
                                className={`search-option ${focusedIndex === index && activeList === 'recent' ? 'focused' : ''}`}
                                id={`recent-option-${index}`}
                                role="option"
                                aria-selected={focusedIndex === index && activeList === 'recent'}
                            >
                                {search.name}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default SearchPanel;